import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";

const ComparisonGraph = ({
  label = "",
  metricNumber = "",
  showMetricNumber = true,
  showHeader = true,
  showFooter = true,
  title = "",
  currentData = { xAxisData: [], yAxisData: [[]] },
  comparisonData = { xAxisData: [], yAxisData: [[]] },
  comparisonPeriod = "",
  showComparison = false,
  onToggleComparison = () => {},
  dashboardOverrideStyles = {},
  // New props for multi-metric support
  allMetricsData = {},
  allMetricsComparisonData = {},
  selectedMetrics = ['revenue'],
  metricLabels = {},
}) => {
  const [chartOption, setChartOption] = useState({});
  
  // Extended color palette for different metrics
  const metricColors = {
    revenue: "#5470C6",
    pendingRevenue: "#9B59B6",
    sessions: "#EE6666", 
    orders: "#73C0DE",
    tickets: "#3BA272",
    conversion: "#FC8452"
  };

  const comparisonColors = {
    revenue: "#91C7FF",
    pendingRevenue: "#D2B4DE",
    sessions: "#FFB5B5",
    orders: "#B3E0FF", 
    tickets: "#7DD3C0",
    conversion: "#FFB38A"
  };

  useEffect(() => {
    updateChartOptions();
  }, [allMetricsData, allMetricsComparisonData, selectedMetrics, showComparison]);

  const updateChartOptions = () => {
    if (!selectedMetrics.length || !allMetricsData || Object.keys(allMetricsData).length === 0) {
      setChartOption({
        title: { text: "No data available" },
      });
      return;
    }

    // Get the longest x-axis data from all selected metrics (not just the first)
    // This ensures the graph shows even if one metric has no data
    let xAxisData = [];
    selectedMetrics.forEach(metricKey => {
      const metricXAxis = allMetricsData[metricKey]?.xAxisData || [];
      if (metricXAxis.length > xAxisData.length) {
        xAxisData = metricXAxis;
      }
    });

    if (!xAxisData.length) {
      setChartOption({
        title: { text: "No data available" },
      });
      return;
    }

    // Build series array for current period
    let series = selectedMetrics.map(metricKey => ({
      name: metricLabels[metricKey] || metricKey,
      type: 'line',
      smooth: true,
      emphasis: {
        focus: 'series'
      },
      data: allMetricsData[metricKey]?.yAxisData || [],
      itemStyle: {
        color: metricColors[metricKey] || '#5470C6'
      },
      lineStyle: {
        color: metricColors[metricKey] || '#5470C6'
      }
    }));

    // Build legend data
    let legendData = selectedMetrics.map(metricKey => metricLabels[metricKey] || metricKey);

    // Add comparison series if enabled
    if (showComparison && allMetricsComparisonData && Object.keys(allMetricsComparisonData).length > 0) {
      const comparisonSeries = selectedMetrics.map(metricKey => ({
        name: `${metricLabels[metricKey] || metricKey} (${comparisonPeriod})`,
        type: 'line',
        smooth: true,
        emphasis: {
          focus: 'series'
        },
        data: allMetricsComparisonData[metricKey]?.yAxisData || [],
        itemStyle: {
          color: comparisonColors[metricKey] || '#91C7FF'
        },
        lineStyle: {
          color: comparisonColors[metricKey] || '#91C7FF',
          type: 'dashed'
        }
      }));
      
      series = [...series, ...comparisonSeries];
      legendData = [
        ...legendData, 
        ...selectedMetrics.map(metricKey => `${metricLabels[metricKey] || metricKey} (${comparisonPeriod})`)
      ];
    }

    // Determine if we need dual y-axis (for revenue vs other metrics)
    const hasRevenue = selectedMetrics.includes('revenue') || selectedMetrics.includes('pendingRevenue');
    const hasOtherMetrics = selectedMetrics.some(metric => metric !== 'revenue' && metric !== 'pendingRevenue');
    const needsDualAxis = hasRevenue && hasOtherMetrics;

    let yAxis;
    if (needsDualAxis) {
      yAxis = [
        {
          type: 'value',
          name: 'Revenue (£)',
          position: 'left',
          axisLabel: {
            formatter: '£{value}'
          },
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          }
        },
        {
          type: 'value',
          name: 'Count',
          position: 'right',
          splitLine: {
            show: false
          }
        }
      ];

      // Assign yAxisIndex to series
      series = series.map(s => {
        if (s.name.toLowerCase().includes('revenue')) {
          return { ...s, yAxisIndex: 0 };
        } else {
          return { ...s, yAxisIndex: 1 };
        }
      });
    } else {
      yAxis = {
        type: 'value',
        name: hasRevenue ? '£' : 'Count',
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      };
    }

    setChartOption({
      color: Object.values(metricColors),
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: function(params) {
          let tooltip = `<div style="font-weight: bold">${params[0].axisValue}</div>`;
          params.forEach(param => {
            const value = param.seriesName.toLowerCase().includes('revenue') 
              ? `£${param.value}` 
              : param.value;
            tooltip += `<div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
              ${param.seriesName}: ${value}
            </div>`;
          });
          return tooltip;
        }
      },
      legend: {
        data: legendData,
        bottom: 0,
        type: 'scroll'
      },
      grid: {
        top: 30,
        bottom: legendData.length > 3 ? 80 : 60,
        left: '3%',
        right: needsDualAxis ? '8%' : '4%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: yAxis,
      series: series
    });
  };

  return (
    <div className={`dashboard-metric ${dashboardOverrideStyles}`}>
      {showHeader && (
        <div className="header">
          {/* <div className="label">
            {selectedMetrics.length > 1 
              ? `Multi-Metric Comparison (${selectedMetrics.length} metrics)` 
              : metricLabels[selectedMetrics[0]] || 'Metric Comparison'}
          </div> */}
          <div className="label"></div> 
          <div className="compare-toggle">
            <label className="toggle-container">
              <span>Compare</span>
              <input
                type="checkbox"
                checked={showComparison}
                onChange={() => onToggleComparison(!showComparison)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      )}

      {showMetricNumber && selectedMetrics.length === 1 && (
        <div className="metric-number">{metricNumber}</div>
      )}

      <ReactECharts
        option={chartOption}
        style={{ height: "400px", width: "100%" }}
        notMerge={true}
      />

      {showFooter && selectedMetrics.length > 0 && (
        <div className="footer">
          <div className="selected-metrics">
            <strong>Selected Metrics:</strong> {selectedMetrics.map(metric => metricLabels[metric] || metric).join(', ')}
          </div>
        </div>
      )}

      <style jsx>{`
        .dashboard-metric {
          background-color: white;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .label {
          font-weight: 600;
          font-size: 16px;
        }
        .metric-number {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 16px;
        }
        .footer {
          display: flex;
          justify-content: center;
          margin-top: 8px;
          font-size: 14px;
          color: #666;
        }
        .selected-metrics {
          text-align: center;
        }
        .comparison-legend span {
          font-weight: bold;
        }
        .compare-toggle {
          display: flex;
          align-items: center;
        }
        .toggle-container {
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        .toggle-container span {
          margin-right: 8px;
          font-size: 14px;
          color: #666;
        }
        .toggle-container input {
          height: 0;
          width: 0;
          visibility: hidden;
          position: absolute;
        }
        .toggle-slider {
          position: relative;
          width: 40px;
          height: 20px;
          background-color: #ccc;
          border-radius: 20px;
          transition: 0.3s;
        }
        .toggle-slider:after {
          content: "";
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          background-color: white;
          border-radius: 50%;
          transition: 0.3s;
        }
        input:checked + .toggle-slider {
          background-color: #5470C6;
        }
        input:checked + .toggle-slider:after {
          left: calc(100% - 2px);
          transform: translateX(-100%);
        }
      `}</style>
    </div>
  );
};

export default ComparisonGraph;